import React, { useEffect, useMemo, useState } from 'react'
import { Tabs as BSTabs } from 'react-bootstrap'
import Tab from 'react-bootstrap/Tab'
import { useTypedTranslation } from 'app/modules/shared/hooks/use-translation'
import { capitalize, hasFields } from '../helpers/functions'
import { GenericTabsOptions } from '../types'
import { renderObjectToDataRows } from '../helpers/object-to-data-rows'
import { useExport } from '../contexts'
import { useLocation, useNavigate } from 'react-router-dom'
import { useURLSearchParams } from 'app/modules/shared'

/**
 * Allow any customization of standard Bootstrap tabs
 */
export function Tabs(props: { [key: string]: any }): JSX.Element {
  return <BSTabs {...props} />
}

type GenericTabsProps = {
  data: { [key: string]: any }
  options: GenericTabsOptions
  mountOnEnter?: boolean
  urlKey?: string
  defaultSelected?: string
}
export function GenericTabs(props: GenericTabsProps): JSX.Element {
  const { data, options, mountOnEnter, urlKey, defaultSelected } = props
  const { _t } = useTypedTranslation()
  const { exporting } = useExport()
  const location = useLocation()
  const navigate = useNavigate()
  const urlParams = useURLSearchParams()

  const defaultTab = (defaultSelected || (urlKey && urlParams.get(urlKey))) ?? getDefaultSelectedTab(options, data)
  const [activeTab, setActiveTab] = useState<string | undefined | null>(defaultTab)

  // Update tab in url search query
  useEffect(() => {
    if (!urlKey || !activeTab) {
      return
    }

    const urlTab = urlParams.get(urlKey)
    if (urlTab === activeTab) {
      return
    }

    activeTab ? urlParams.set(urlKey, activeTab) : urlParams.delete(urlKey)

    navigate(location.pathname + '?' + urlParams.toString())
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, urlKey])

  const { validOptions, empty } = useMemo(() => {
    const valid: GenericTabsOptions = {}
    let empty = true

    Object.entries(options).forEach(([name, value]) => {
      if (validateTabData(data, name)) {
        valid[name] = value
        empty = false
      }
    })

    return { validOptions: valid, empty }
  }, [data, options])

  if (empty) {
    return <i>{_t('no-data')}</i>
  }

  if (exporting) {
    return (
      <>
        {Object.keys(validOptions).map((name: string, idx: number) => {
          const tabOptions = validOptions[name]
          const handler = tabOptions.handler ?? defaultTabHandler

          return (
            <section className="html-tab mb-4" key={idx}>
              <h3 className="tab-title h4 mb-2">{tabOptions.title || capitalize(name)}</h3>
              {handler(data[name], _t)}
            </section>
          )
        })}
      </>
    )
  }

  return (
    <Tabs
      className="mb-3"
      mountOnEnter={!!mountOnEnter}
      activeKey={activeTab}
      onSelect={(key: string | null) => setActiveTab(key as string)}
    >
      {Object.entries(validOptions).map(([name, options], idx: number) => {
        const title = options.title || capitalize(name).replace('_', ' ')
        const handler = options.handler ?? defaultTabHandler
        const tabClassName = options.capitalize === false ? 'text-default' : undefined

        if (handler(data[name], _t) === null) {
          return null
        }

        return (
          <Tab key={idx} eventKey={name} title={title} tabClassName={tabClassName}>
            {handler(data[name], _t)}
          </Tab>
        )
      })}
    </Tabs>
  )
}

/**
 * Default simple function for building tab content
 *
 * @param data
 */
function defaultTabHandler(data: { [key: string]: string }) {
  return renderObjectToDataRows(data, false, false, 1024)
}

function getDefaultSelectedTab(options: any, data: any) {
  let result
  for (const name in options) {
    if (validateTabData(data, name)) {
      result = name
      break
    }
  }

  return result
}

// Check if tab data is valid to even show a tab for it
function validateTabData(data: { [key: string]: any }, name: string) {
  return (
    (Array.isArray(data[name]) && data[name].length !== 0) || (typeof data[name] === 'object' && hasFields(data[name]))
  )
}
